import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '../config/supabase';
import { trackEvent } from '../utils/analytics';
import { DEBUG } from '../config/debug';
import { setSentryUser } from '../utils/sentry';
import { createContact } from '../services/hubspot/hubspotService';
import * as Sentry from '@sentry/react';
import {
  getCampaignAttributionForHubSpot,
  getCampaignAttributionForSignupEvent,
} from '../utils/campaignAttribution';

interface Subscription {
  id: string;
  user_id: string;
  stripe_subscription_id: string;
  stripe_customer_id: string;
  status:
    | 'trialing'
    | 'active'
    | 'canceled'
    | 'past_due'
    | 'unpaid'
    | 'incomplete'
    | 'incomplete_expired';
  tier: 'free' | 'essential' | 'professional' | 'elite' | 'beta_founder';
  price_id: string;
  current_period_start: Date;
  current_period_end: Date;
  trial_end?: Date;
  canceled_at?: Date;
  amount: number;
  created_at: Date;
  updated_at: Date;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  subscription: Subscription | null;
  loading: boolean;
  signUp: (email: string, password: string, metadata?: { data: any }) => Promise<any>;
  signIn: (email: string, password: string) => Promise<any>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<any>;
  refreshSubscription: () => Promise<void>;
}

const testAuthValue: AuthContextType = {
  user: {
    id: 'test-user',
    email: 'test@glyyd.com',
  } as User,
  session: null,
  subscription: null,
  loading: false,
  signUp: async () => ({}),
  signIn: async () => ({}),
  signOut: async () => {},
  resetPassword: async () => ({}),
  refreshSubscription: async () => {},
};

const REGISTRATION_SOURCES: Record<string, string> = {
  coach: 'Coach Registration',
  rower: 'Rower Registration',
  parent: 'Parent Registration',
};

/** HubSpot source label for a signup, keyed by the role in signup metadata. */
const getRegistrationSource = (role: unknown): string =>
  (typeof role === 'string' && REGISTRATION_SOURCES[role]) || 'Coach Registration';

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    if (process.env.NODE_ENV === 'test') {
      return testAuthValue;
    }
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);

  // Helper function to identify user with Sentry
  const identifyUser = (user: User | null) => {
    if (user?.id) {
      const role =
        typeof user.user_metadata?.role === 'string' ? user.user_metadata.role : undefined;
      setSentryUser({
        id: user.id,
        role,
      });
    } else {
      setSentryUser(null);
    }
  };

  // Fetch subscription data for authenticated user
  const fetchSubscription = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();

      if (error) {
        if (error.code === 'PGRST116') {
          // No subscription found - user is on free tier
          setSubscription(null);
        } else {
          console.error('Subscription fetch error:', error);
          // Report subscription fetch errors to Sentry
          Sentry.captureException(new Error(`Subscription fetch failed: ${error.message}`), {
            tags: { component: 'auth-context', operation: 'fetch_subscription' },
            extra: {
              userId: userId.substring(0, 8) + '***', // Partial user ID for privacy
              errorCode: error.code,
              errorMessage: error.message,
            },
          });
          setSubscription(null);
        }
      } else if (data) {
        // Convert date strings to Date objects
        const subscriptionData: Subscription = {
          id: data.id as string,
          user_id: data.user_id as string,
          stripe_subscription_id: data.stripe_subscription_id as string,
          stripe_customer_id: data.stripe_customer_id as string,
          status: data.status as Subscription['status'],
          tier: data.tier as Subscription['tier'],
          price_id: data.price_id as string,
          amount: data.amount as number,
          current_period_start: new Date(data.current_period_start as string),
          current_period_end: new Date(data.current_period_end as string),
          trial_end: data.trial_end ? new Date(data.trial_end as string) : undefined,
          canceled_at: data.canceled_at ? new Date(data.canceled_at as string) : undefined,
          created_at: new Date(data.created_at as string),
          updated_at: new Date(data.updated_at as string),
        };
        setSubscription(subscriptionData);
      }
    } catch (error) {
      console.error('Subscription fetch exception:', error);
      setSubscription(null);
    }
  };

  // Manual refresh function for subscription data
  const refreshSubscription = useCallback(async () => {
    if (user) {
      await fetchSubscription(user.id);
    }
  }, [user]);

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      identifyUser(session?.user ?? null);

      // Fetch subscription if user is authenticated
      if (session?.user) {
        fetchSubscription(session.user.id);
      } else {
        setSubscription(null);
      }

      setLoading(false);
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      identifyUser(session?.user ?? null);

      // Fetch subscription if user is authenticated, clear if not
      if (session?.user) {
        fetchSubscription(session.user.id);
      } else {
        setSubscription(null);
      }

      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = useCallback(async (email: string, password: string, metadata?: { data: any }) => {
    try {
      DEBUG.log('AUTH', 'SignUp attempt with:', {
        email,
        metadataKeys: Object.keys(metadata?.data || {}),
      });

      const siteUrl =
        process.env.REACT_APP_SITE_URL?.trim() ||
        process.env.REACT_APP_BASE_URL?.trim() ||
        window.location.origin;

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${siteUrl}/welcome`,
          data: metadata?.data,
        },
      });

      if (error) {
        console.error('Supabase signUp error:', error);
        // Report sign-up errors to Sentry
        Sentry.captureException(new Error(`Sign-up failed: ${error.message}`), {
          tags: { component: 'auth-context', operation: 'sign_up' },
          extra: {
            email: email.substring(0, 3) + '***', // Partial email for privacy
            errorCode: error.status,
            errorMessage: error.message,
            metadataKeys: Object.keys(metadata?.data || {}),
          },
        });
      } else if (data?.user) {
        const signupRole = metadata?.data?.role;
        trackEvent.signUp('email', {
          ...(typeof signupRole === 'string' ? { role: signupRole } : {}),
          ...getCampaignAttributionForSignupEvent(),
        });
        identifyUser(data.user);

        // Track registration in HubSpot by role (don't fail if HubSpot fails)
        try {
          const userMetadata = metadata?.data || {};
          const campaignAttribution = getCampaignAttributionForHubSpot();
          await createContact({
            email: email.toLowerCase().trim(),
            firstName: userMetadata.firstName || userMetadata.first_name || 'Coach',
            lastName: userMetadata.lastName || userMetadata.last_name || 'User',
            source: getRegistrationSource(userMetadata.role),
            ...campaignAttribution,
          });
        } catch (hubspotError) {
          console.warn('Failed to track registration in HubSpot:', hubspotError);
          // Report HubSpot errors to Sentry (non-critical)
          Sentry.captureException(hubspotError, {
            tags: { component: 'auth-context', operation: 'hubspot_tracking' },
            extra: {
              email: email.substring(0, 3) + '***',
              context: 'signup_hubspot_tracking',
            },
          });
          // Don't fail signup if HubSpot fails
        }
      }

      return { data, error };
    } catch (err) {
      console.error('SignUp exception:', err);
      // Report unexpected sign-up errors to Sentry
      Sentry.captureException(err, {
        tags: { component: 'auth-context', operation: 'sign_up_unexpected' },
        extra: {
          email: email.substring(0, 3) + '***', // Partial email for privacy
          metadataKeys: Object.keys(metadata?.data || {}),
        },
      });
      return { data: null, error: err };
    }
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (!error && data.user) {
        trackEvent.signIn('email');
        identifyUser(data.user);
      } else if (error) {
        // Report sign-in errors to Sentry
        Sentry.captureException(new Error(`Sign-in failed: ${error.message}`), {
          tags: { component: 'auth-context', operation: 'sign_in' },
          extra: {
            email: email.substring(0, 3) + '***', // Partial email for privacy
            errorCode: error.status,
            errorMessage: error.message,
          },
        });
      }

      return { data, error };
    } catch (err) {
      // Report unexpected sign-in errors to Sentry
      Sentry.captureException(err, {
        tags: { component: 'auth-context', operation: 'sign_in_unexpected' },
        extra: {
          email: email.substring(0, 3) + '***', // Partial email for privacy
        },
      });
      throw err;
    }
  }, []);

  const signOut = useCallback(async () => {
    // Clear React Query cache before signing out to prevent flickering
    const { queryClient } = await import('../config/queryClient');
    queryClient.clear();

    const { clearAllCoachStorage } = await import('../utils/coachStorage');
    clearAllCoachStorage();

    await supabase.auth.signOut();
    setSubscription(null);
    trackEvent.signOut();
  }, []);

  const resetPassword = useCallback(async (email: string) => {
    try {
      const isLocalhost =
        typeof window !== 'undefined' &&
        (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
      const apiUrl = isLocalhost
        ? 'http://localhost:3001/api/auth/send-password-reset'
        : '/api/auth/send-password-reset';

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        let errorMessage = 'Something went wrong sending the reset link. Please try again.';

        try {
          const errorBody = await response.json();
          errorMessage =
            errorBody?.error ||
            errorBody?.message ||
            `${errorMessage} (${response.status} ${response.statusText})`;
        } catch (jsonErr) {
          console.warn('Failed to parse password reset error response', jsonErr);
        }

        return {
          data: null,
          error: { message: errorMessage },
        };
      }

      const payload = await response.json().catch(() => ({ success: true }));
      const message =
        payload?.message ||
        "If we find an account with that email, we'll send password reset instructions shortly.";
      return { data: { ...payload, message }, error: null };
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : 'Something went wrong sending the reset link. Please try again.';
      console.error('Password reset request failed:', err);
      return {
        data: null,
        error: { message },
      };
    }
  }, []);

  const value = useMemo(
    () => ({
      user,
      session,
      subscription,
      loading,
      signUp,
      signIn,
      signOut,
      resetPassword,
      refreshSubscription,
    }),
    [
      user,
      session,
      subscription,
      loading,
      signUp,
      signIn,
      signOut,
      resetPassword,
      refreshSubscription,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
